import { useCallback, useMemo } from 'react';

import { ColumnDef, Updater, VisibilityState } from '@tanstack/react-table';

import useLocalStorage from '@/hooks/useLocalStorage';

const EMPTY_VISIBILITY: VisibilityState = {};

export const useColumnVisibility = <TData = unknown>(
  columns: ColumnDef<TData>[],
  storageKey: string
) => {
  const [storedVisibility, setStoredVisibility] = useLocalStorage<VisibilityState>(
    storageKey,
    EMPTY_VISIBILITY
  );

  const hideableColumns = useMemo(() => {
    return columns.filter((col) => col.enableHiding !== false);
  }, [columns]);

  const defaultVisibility = useMemo(() => {
    const defaults: VisibilityState = {};
    for (const col of hideableColumns) {
      if (col.id && col.meta?.defaultHidden) {
        defaults[col.id] = false;
      }
    }
    return defaults;
  }, [hideableColumns]);

  const columnVisibility = useMemo(() => {
    if (Object.keys(defaultVisibility).length === 0) {
      return storedVisibility;
    }
    return { ...defaultVisibility, ...storedVisibility };
  }, [defaultVisibility, storedVisibility]);

  const setColumnVisibility = useCallback(
    (value: Updater<VisibilityState>) => {
      setStoredVisibility(typeof value === 'function' ? value(columnVisibility) : value);
    },
    [setStoredVisibility, columnVisibility]
  );

  const resetColumnVisibility = useCallback(() => {
    setStoredVisibility(EMPTY_VISIBILITY);
  }, [setStoredVisibility]);

  const hasHiddenColumns = useMemo(() => {
    // The stored state can hold columns that this table does not render, e.g. a shared storage key.
    const columnIds = new Set(
      hideableColumns.map((col) => col.id ?? ('accessorKey' in col ? String(col.accessorKey) : ''))
    );
    return Object.entries(columnVisibility).some(
      ([id, visible]) => visible === false && defaultVisibility[id] !== false && columnIds.has(id)
    );
  }, [columnVisibility, defaultVisibility, hideableColumns]);

  return {
    columnVisibility,
    hideableColumns,
    setColumnVisibility,
    resetColumnVisibility,
    hasHiddenColumns,
  };
};
